import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { masterApi } from '../../api/masterApi';

export const SupplierAuthEditor = ({ isOpen, onClose, item, action, onSave }) => {
  const [formData, setFormData] = useState({
    supplier_code: '',
    password: '',
    new_password: '',
    confirm_password: '',
    password_mode: 'manual',
    autoGenerate: false,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);

  useEffect(() => {
    if (action === 'create') {
      setFormData({
        supplier_code: '',
        password: '',
        new_password: '',
        confirm_password: '',
        password_mode: 'manual',
        autoGenerate: false,
      });
      setSupplierSearch('');
      setSupplierSuggestions([]);
    } else if (action === 'password' && item) {
      setFormData({
        supplier_code: item.supplier_code,
        password: '',
        new_password: '',
        confirm_password: '',
        password_mode: 'manual',
        autoGenerate: false,
      });
      setSupplierSearch('');
      setSupplierSuggestions([]);
    }
    setErrors({});
  }, [item, action, isOpen]);

  // 仕入先サジェスト取得
  useEffect(() => {
    if (!supplierSearch || supplierSearch.length < 2) {
      setSupplierSuggestions([]);
      return;
    }

    const handler = setTimeout(async () => {
      try {
        const data = await masterApi.getSuppliers(supplierSearch);
        setSupplierSuggestions(data);
      } catch (e) {
        console.error('仕入先サジェスト取得エラー', e);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [supplierSearch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validatePassword = (password, supplierCode) => {
    if (!password) return 'パスワードを入力してください';
    if (password.length < 12) return 'パスワードは12文字以上である必要があります';
    if (!/[A-Z]/.test(password)) return 'パスワードには大文字を含む必要があります';
    if (!/[a-z]/.test(password)) return 'パスワードには小文字を含む必要があります';
    if (!/[0-9]/.test(password)) return 'パスワードには数字を含む必要があります';
    if (supplierCode && password.includes(String(supplierCode))) {
      return 'パスワードに仕入先コードを含めることはできません';
    }
    return null;
  };

  const validate = () => {
    const newErrors = {};
    
    if (action === 'create') {
      if (!formData.supplier_code) {
        newErrors.supplier_code = '仕入先コードを選択してください';
      }
      if (!formData.autoGenerate) {
        const passwordError = validatePassword(formData.password, parseInt(formData.supplier_code));
        if (passwordError) {
          newErrors.password = passwordError;
        }
      }
    } else if (action === 'password') {
      if (formData.password_mode === 'manual') {
        const passwordError = validatePassword(formData.new_password, item?.supplier_code);
        if (passwordError) {
          newErrors.new_password = passwordError;
        }
        if (!formData.confirm_password) {
          newErrors.confirm_password = '確認用パスワードを入力してください';
        } else if (formData.new_password !== formData.confirm_password) {
          newErrors.confirm_password = '確認用パスワードが一致しません';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setSaving(true);
    try {
      if (action === 'create') {
        await onSave({
          supplier_code: parseInt(formData.supplier_code),
          password: formData.autoGenerate ? null : formData.password,
        });
      } else if (action === 'password') {
        await onSave({
          mode: formData.password_mode,
          new_password: formData.password_mode === 'manual' ? formData.new_password : null,
        });
      }
      onClose();
    } catch (error) {
      alert(error.response?.data?.detail || error.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const getTitle = () => {
    if (action === 'create') return '仕入先認証情報作成';
    if (action === 'password') return 'パスワード変更';
    return '仕入先認証情報編集';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={getTitle()}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {action === 'create' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              仕入先検索 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.supplier_code ? 'border-red-300' : 'border-slate-300'
              }`}
              placeholder="仕入先CDまたは名称の一部を入力"
            />
            {supplierSuggestions.length > 0 && (
              <div className="mt-1 max-h-48 overflow-auto border border-slate-300 rounded-md bg-white shadow-sm">
                {supplierSuggestions.map((s) => (
                  <button
                    type="button"
                    key={s.supplier_code}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        supplier_code: s.supplier_code,
                      }));
                      setSupplierSearch(`${s.supplier_code} - ${s.supplier_name}`);
                      setSupplierSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-1 text-sm hover:bg-slate-100"
                  >
                    {s.supplier_code} - {s.supplier_name}
                  </button>
                ))}
              </div>
            )}
            {errors.supplier_code && (
              <p className="mt-1 text-sm text-red-600">{errors.supplier_code}</p>
            )}
          </div>
        )}

        {action === 'create' && (
          <>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="autoGenerate"
                  checked={formData.autoGenerate}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-slate-700">初期パスワードを自動生成</span>
              </label>
            </div>
            {!formData.autoGenerate && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  初期パスワード <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${
                    errors.password ? 'border-red-300' : 'border-slate-300'
                  }`}
                  required
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  要件: 12文字以上、大文字・小文字・数字を含む、仕入先コードを含まない
                </p>
              </div>
            )}
          </>
        )}

        {action === 'password' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                設定方法
              </label>
              <select
                name="password_mode"
                value={formData.password_mode}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="manual">手入力</option>
                <option value="temp">自動生成</option>
              </select>
            </div>

            {formData.password_mode === 'manual' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    新パスワード <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.new_password ? 'border-red-300' : 'border-slate-300'
                    }`}
                    required
                  />
                  {errors.new_password && (
                    <p className="mt-1 text-sm text-red-600">{errors.new_password}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    新パスワード（確認） <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.confirm_password ? 'border-red-300' : 'border-slate-300'
                    }`}
                    required
                  />
                  {errors.confirm_password && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirm_password}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">
                    要件: 12文字以上、大文字・小文字・数字を含む、仕入先コードを含まない
                  </p>
                </div>
              </>
            ) : null}

            <p className="text-xs text-slate-500">
              設定したパスワードは再表示できません。必ずこの画面で控えてください。
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300"
          >
            キャンセル
          </button>
        </div>
      </form>
    </Modal>
  );
};
